// Pricing Service - Helper functions for user plan management
import { PLAN_PUBLIC_IDS, PLAN_ID_MAP } from "./planIds";
import { PRICING_PLANS, BILLING_CYCLES, MOST_POPULAR_PLAN, SUBSCRIPTION_STATUS } from "./data";

// Get plan by public_id key (e.g., "paddle_bronze_monthly")
export const getPlanByKey = (publicId) => {
  if (!publicId) return null;

  // Search through all plans to find matching key
  for (const [, plan] of Object.entries(PRICING_PLANS)) {
    if (plan.key?.monthly === publicId || plan.key?.yearly === publicId) {
      return plan;
    }
  }
  return null;
};

// Get current plan key (returns full public_id like "paddle_bronze_monthly")
export const getCurrentPlanKey = (user) => {
  if (!user?.plan) return null;
  return user.plan.public_id || null;
};


// Check if plan is most popular
export const isMostPopularPlan = (planKey) => planKey === MOST_POPULAR_PLAN;

// Check if user is on legacy plan
export function isLegacyPlan(user) {
  if (!user?.plan) return false;
  const publicId = user.plan.public_id;
  const newPlans = [
    PLAN_PUBLIC_IDS.paddle_bronze_monthly,
    PLAN_PUBLIC_IDS.paddle_bronze_yearly,
    PLAN_PUBLIC_IDS.paddle_silver_monthly,
    PLAN_PUBLIC_IDS.paddle_silver_yearly,
    PLAN_PUBLIC_IDS.paddle_gold_monthly,
    PLAN_PUBLIC_IDS.paddle_gold_yearly,
    PLAN_PUBLIC_IDS.free,
    PLAN_PUBLIC_IDS.freeTrial,
  ];
  return !newPlans.includes(publicId);
}

// Get billing cycle config
export const getBillingCycleConfig = (isMonthly = true) =>
  isMonthly ? BILLING_CYCLES.MONTHLY : BILLING_CYCLES.YEARLY;

// Get plan with specific billing cycle
export const getPlanWithBilling = (key, billing = "monthly") => {
  const plan = PRICING_PLANS[key];
  if (!plan) return null;
  return { ...plan, price: plan.pricing[billing], billing };
};

// Format date for display
export function formatDate(date) {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : new Date(date * 1000);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// get subscription status from user
export const getSubscriptionStatus = (user) => {
  if (!user?.plan) return SUBSCRIPTION_STATUS.NONE;
  const { plan } = user;
  const sub = plan.subscription;
  if (sub) {
    // Check past due first (before active) - priority order matters
    if (sub.status === SUBSCRIPTION_STATUS.PAST_DUE ) return SUBSCRIPTION_STATUS.PAST_DUE;
    // Check paused: status is paused OR has paused_at OR has paused_from (scheduled to pause)
    // According to Paddle docs, paused_from means subscription is scheduled to pause
    if (sub.status === SUBSCRIPTION_STATUS.PAUSED || sub.paused_at || sub.paused_from || plan.paused_from) {
      return SUBSCRIPTION_STATUS.PAUSED;
    }
    if (sub.status === SUBSCRIPTION_STATUS.DELETED || sub.cancelled_at) return SUBSCRIPTION_STATUS.DELETED;
    if (sub.status === SUBSCRIPTION_STATUS.ACTIVE || sub.active) return SUBSCRIPTION_STATUS.ACTIVE;
  }
  return SUBSCRIPTION_STATUS.NONE;
}

// Status config mapping (single source of truth)
const STATUS_CONFIGS = {
  [SUBSCRIPTION_STATUS.ACTIVE]: { text: 'Active', color: 'success' },
  [SUBSCRIPTION_STATUS.PAUSED]: { text: 'Paused', color: 'warning' },
  [SUBSCRIPTION_STATUS.DELETED]: { text: 'Cancelled', color: 'error' },
  [SUBSCRIPTION_STATUS.PAST_DUE]: { text: 'Past Due', color: 'warning' },
  [SUBSCRIPTION_STATUS.NONE]: { text: 'None', color: 'primary' }
}

// Get status config for UI
export const getStatusConfig = (status) => {
  return STATUS_CONFIGS[status] || STATUS_CONFIGS[SUBSCRIPTION_STATUS.NONE]
}

export const getPlanPrice = (publicId, isMonthly = true) => {
  const plan = getPlanByKey(publicId)
  if (!plan?.pricing) return 0
  const cycle = isMonthly ? BILLING_CYCLES.MONTHLY : BILLING_CYCLES.YEARLY
  // Handle legacy plans that might not have both monthly/yearly pricing
  const price = plan.pricing[cycle.key]
  if (price === null || price === undefined) {
    // Fallback to the other billing cycle if available
    const fallbackCycle = isMonthly ? BILLING_CYCLES.YEARLY : BILLING_CYCLES.MONTHLY
    return plan.pricing[fallbackCycle.key] || 0
  }
  return price
}

export const formatPlanPrice = (price, isMonthly = true) => {
  const cycle = isMonthly ? BILLING_CYCLES.MONTHLY : BILLING_CYCLES.YEARLY
  return `$${price}/${cycle.interval}`
}

export const getSubscriptionInfo = (user) => {
  if (!user?.plan) {
    const config = getStatusConfig(SUBSCRIPTION_STATUS.NONE)
    return {
      status: SUBSCRIPTION_STATUS.NONE,
      statusText: 'No Subscription',
      statusColor: config.color,
      isLegacy: false
    }
  }

  const status = getSubscriptionStatus(user)
  const isLegacy = isLegacyPlan(user)
  const config = getStatusConfig(status)

  return {
    status,
    statusText: config.text,
    statusColor: config.color,
    isLegacy
  }
}

// Get Paddle product ID from public_id (which now includes billing cycle)
export const getPaddleProductId = (publicId) => {
  if (!publicId) return null
  // Look up in PLAN_ID_MAP
  return PLAN_ID_MAP[publicId] || null
}

// Check if two plans are the same tier but different billing cycles
export const isSameTierDifferentBilling = (publicId1, publicId2) => {
  if (!publicId1 || !publicId2) return false
  const plan = getPlanByKey(publicId1)
  return plan && plan === getPlanByKey(publicId2) && publicId1.includes('_monthly') !== publicId2.includes('_monthly')
}
